import { deepMerge } from '@mantine/core';
import { MRT_ColumnDef, MRT_TableOptions } from 'mantine-react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import useAdminStats from '../../data-access/hooks/use-admin-stats/admin-stats';
import { AdminStatsFarm } from '../../data-access/types/stats';

import { useFiltersTableOptions } from '@data-access';
import { PfTable } from '@ui';

export function FarmsDataGrid() {
  const { t } = useTranslation();
  const filtersTableOptions =
    useFiltersTableOptions<AdminStatsFarm>('adminStats');
  const stats = useAdminStats();

  const tableColumns = useMemo<MRT_ColumnDef<AdminStatsFarm>[]>(
    () => [
      {
        accessorKey: 'last_name',
        accessorFn: (row) => `${row.last_name} ${row.first_name}`,
        header: t('common.properties.last_name'),
      },
      {
        accessorKey: 'name',
        header: t('field.form.farmLabel'),
      },
      {
        accessorKey: 'organization',
        header: t('common.properties.organization'),
      },
      {
        accessorKey: 'country',
        header: t('forms.countries_label'),
      },
      {
        accessorKey: 'total_area',
        Cell: ({ cell }) => `${Number(cell.getValue<string>()).toFixed(2)} ha`,
        header: t('common.properties.total_ha'),
        filterVariant: 'range',
        filterFn: 'inNumberRange',
      },
      {
        accessorKey: 'forms1',
        minSize: 250,
        Cell: ({ cell }) => `${cell.getValue<number>()} %`,
        header: t('common.completed_fields_percent') + ' (1)',
        filterVariant: 'range',
        filterFn: 'inNumberRange',
      },
      {
        accessorKey: 'forms1_area',
        minSize: 250,
        Cell: ({ cell }) => `${cell.getValue<number>()?.toFixed(2)} ha`,
        header: t('common.completed_fields_area') + ' (1)',
        filterVariant: 'range',
        filterFn: 'inNumberRange',
      },
      {
        accessorKey: 'forms2',
        minSize: 250,
        Cell: ({ cell }) => `${cell.getValue<number>()} %`,
        header: t('common.completed_fields_percent') + ' (2)',
        filterVariant: 'range',
        filterFn: 'inNumberRange',
      },
      {
        accessorKey: 'forms2_area',
        minSize: 250,
        Cell: ({ cell }) => `${cell.getValue<number>()?.toFixed(2)} ha`,
        header: t('common.completed_fields_area') + ' (2)',
        filterVariant: 'range',
        filterFn: 'inNumberRange',
      },
      {
        accessorKey: 'finished_forms',
        minSize: 220,
        Cell: ({ cell }) => `${cell.getValue<number>()} %`,
        header: t('common.finished_forms_percent'),
        filterVariant: 'range',
        filterFn: 'inNumberRange',
      },
      {
        accessorKey: 'finished_area',
        Cell: ({ cell }) => `${cell.getValue<number>()?.toFixed(2)} ha`,
        header: t('common.finished_forms_area'),
        filterVariant: 'range',
        filterFn: 'inNumberRange',
      },
    ],
    [t],
  );

  const tableOptions: MRT_TableOptions<AdminStatsFarm> = deepMerge<
    MRT_TableOptions<AdminStatsFarm>
  >(
    {
      columns: tableColumns,
      data: stats.data?.farms.results ?? [],
      rowCount: stats.data?.farms.count ?? 0,

      state: {
        isLoading: stats.isLoading,
      },
      initialState: {
        showColumnFilters: true,
      },

      enableRowActions: false,
    },
    filtersTableOptions,
  );

  return <PfTable options={tableOptions} />;
}

export default FarmsDataGrid;
